import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { waitlistAPI, serviceAPI, staffAPI } from '../lib/api';
import { toast } from 'sonner';
import { formatShortDate } from '../lib/utils';
import { 
  Users, 
  Clock, 
  Phone, 
  Mail, 
  CheckCircle, 
  XCircle,
  Calendar,
  User,
  Scissors,
  RefreshCw,
  MessageSquare
} from 'lucide-react';

export default function WaitlistSection() {
  const [entries, setEntries] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [entriesRes, servicesRes, staffRes] = await Promise.all([
        waitlistAPI.list(statusFilter || undefined).catch(() => ({ data: [] })),
        serviceAPI.list().catch(() => ({ data: [] })),
        staffAPI.list().catch(() => ({ data: [] }))
      ]);
      
      setEntries(entriesRes.data || []);
      setServices(servicesRes.data || []);
      setStaff(staffRes.data || []);
    } catch (error) {
      console.error('Errore caricamento lista attesa:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (entryId, status) => {
    try {
      await waitlistAPI.updateStatus(entryId, status);
      toast.success(status === 'contacted' ? 'Contrassegnato come contattato' : 
                    status === 'booked' ? 'Contrassegnato come prenotato' : 
                    'Stato aggiornato');
      loadData();
    } catch (error) {
      toast.error('Errore aggiornamento stato');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Rimuovere questa richiesta dalla lista d\'attesa?')) return;
    
    try {
      await waitlistAPI.delete(entryId);
      toast.success('Richiesta rimossa');
      loadData();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const getServiceName = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    return service?.name || 'Servizio non specificato';
  };

  const getStaffName = (staffId) => {
    if (!staffId) return 'Qualsiasi operatore';
    const member = staff.find(s => s.id === staffId);
    return member?.user?.name || 'Operatore';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500">In attesa</Badge>;
      case 'contacted':
        return <Badge className="bg-blue-500">Contattato</Badge>;
      case 'booked':
        return <Badge className="bg-green-500">Prenotato</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Annullato</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatPreferredTimes = (times) => {
    if (!times || times.length === 0) return 'Qualsiasi orario';
    const labels = {
      'morning': 'Mattina',
      'afternoon': 'Pomeriggio',
      'evening': 'Sera'
    };
    return times.map(t => labels[t] || t).join(', ');
  };

  const pendingCount = entries.filter(e => e.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid sm:grid-cols-4 gap-4">
        <Card 
          className={`cursor-pointer transition-all ${statusFilter === 'pending' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setStatusFilter('pending')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Attesa</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {entries.filter(e => e.status === 'pending').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all ${statusFilter === 'contacted' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setStatusFilter('contacted')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Contattati</p>
                <p className="text-2xl font-bold text-blue-600">
                  {entries.filter(e => e.status === 'contacted').length}
                </p>
              </div>
              <Phone className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all ${statusFilter === 'booked' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setStatusFilter('booked')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Prenotati</p>
                <p className="text-2xl font-bold text-green-600">
                  {entries.filter(e => e.status === 'booked').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all ${statusFilter === '' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setStatusFilter('')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totale</p>
                <p className="text-2xl font-bold">{entries.length}</p>
              </div>
              <Users className="w-8 h-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Lista d'Attesa
              {pendingCount > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {pendingCount} da gestire
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Gestisci le richieste di prenotazione in attesa
            </CardDescription>
          </div>
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="spinner" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {statusFilter 
                  ? `Nessuna richiesta con stato "${statusFilter}"`
                  : 'Nessuna richiesta in lista d\'attesa'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {entries.map((entry) => (
                <Card key={entry.id} className="bg-muted/30" data-testid={`waitlist-entry-${entry.id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      {/* User Info */}
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <User className="w-6 h-6 text-primary" />
                        </div>
                        <div className="space-y-1">
                          <p className="font-semibold">{entry.user_name || entry.guest_name || 'Cliente'}</p>
                          <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                            {entry.user_email && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {entry.user_email}
                              </span>
                            )}
                            {entry.user_phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {entry.user_phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Request Details */}
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <Scissors className="w-4 h-4 text-muted-foreground" />
                          <span>{getServiceName(entry.service_id)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span>{getStaffName(entry.preferred_staff_id)}</span>
                        </div>
                        {entry.preferred_date && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            <span>{formatShortDate(entry.preferred_date)}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span>{formatPreferredTimes(entry.preferred_times)}</span>
                        </div>
                      </div>

                      {/* Status and Actions */}
                      <div className="flex items-center gap-3">
                        {getStatusBadge(entry.status)}
                        
                        <Select 
                          value={entry.status}
                          onValueChange={(value) => handleUpdateStatus(entry.id, value)}
                        >
                          <SelectTrigger className="w-36" data-testid={`status-select-${entry.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">In attesa</SelectItem>
                            <SelectItem value="contacted">Contattato</SelectItem>
                            <SelectItem value="booked">Prenotato</SelectItem>
                            <SelectItem value="cancelled">Annullato</SelectItem>
                          </SelectContent>
                        </Select>

                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(entry.id)}
                          data-testid={`delete-waitlist-${entry.id}`}
                        >
                          <XCircle className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>

                    {/* Notes */}
                    {entry.notes && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="flex items-start gap-2 text-sm">
                          <MessageSquare className="w-4 h-4 text-muted-foreground mt-0.5" />
                          <p className="text-muted-foreground">{entry.notes}</p>
                        </div>
                      </div>
                    )}

                    {/* Created Date */}
                    <div className="mt-2 text-xs text-muted-foreground">
                      Richiesta del {new Date(entry.created_at).toLocaleDateString('it-IT', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
